import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthShell } from "@/pages/Login";
import { GoogleButton } from "@/components/GoogleButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "", discount_code: (params.get("code") || "").toUpperCase(), whatsapp: "", marketing_consent: false });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Workspace created. Welcome to Legalis AI.");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-serif text-3xl font-semibold">Create your workspace</h1>
      <p className="text-muted-foreground text-sm mt-2">Start free. No card required.</p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-none" placeholder="Jane Counsel" data-testid="register-name" />
        </div>
        <div>
          <Label htmlFor="org">Organization name</Label>
          <Input id="org" value={form.organization_name} onChange={set("organization_name")} className="mt-1.5 rounded-none" placeholder="Acme Legal (optional)" data-testid="register-org" />
        </div>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 rounded-none" placeholder="you@company.com" data-testid="register-email" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="mt-1.5 rounded-none" placeholder="At least 8 characters" data-testid="register-password" />
        </div>
        <div>
          <Label htmlFor="whatsapp">WhatsApp number <span className="text-muted-foreground">(optional)</span></Label>
          <Input id="whatsapp" value={form.whatsapp} onChange={set("whatsapp")} className="mt-1.5 rounded-none" placeholder="+358 40 123 4567" data-testid="register-whatsapp" />
          <p className="text-[11px] text-muted-foreground mt-1">We'll only use this for helpful updates about your account.</p>
        </div>
        <div>
          <Label htmlFor="code">Discount / startup code <span className="text-muted-foreground">(optional)</span></Label>
          <Input id="code" value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })} className="mt-1.5 rounded-none font-mono uppercase" placeholder="STARTUP2026" data-testid="register-code" />
          <p className="text-[11px] text-brand mt-1">Have a code? Unlock a free Enterprise-level plan — no card needed.</p>
        </div>
        <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="register-consent-label">
          <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} className="mt-0.5" data-testid="register-consent" />
          <span>Send me product news and legal-intelligence briefings. Optional, and you can change it anytime in Profile.</span>
        </label>
        <Button type="submit" className="w-full rounded-none" disabled={loading} data-testid="register-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create workspace"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="h-px flex-1 bg-border" />
        <span className="overline text-muted-foreground">or</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton label="Sign up with Google" />

      <p className="text-sm text-muted-foreground mt-6">
        Already have an account? <Link to="/login" className="text-foreground underline underline-offset-4" data-testid="to-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
